import { CheckCircle2, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Button from "@/components/ui/Button";

interface Props {
  value: string;
  onChange: (dataUrl: string) => void;
}

interface Point {
  x: number;
  y: number;
}

const INK_COLOR = "#000000"; // Deep dark black ink
const INK_WIDTH = 2.5;

export default function SignaturePad({ value, onChange }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<Point | null>(null);
  const [signed, setSigned] = useState(Boolean(value));

  function setInkContext(ctx: CanvasRenderingContext2D) {
    ctx.lineWidth = INK_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = INK_COLOR;
    ctx.fillStyle = INK_COLOR;
  }

  function drawBaseline(ctx: CanvasRenderingContext2D, w: number) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(0, 172);
    ctx.lineTo(w, 172);
    ctx.strokeStyle = "#E2E8F0";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;

    function resize() {
      const rect = wrap!.getBoundingClientRect();
      const w = Math.max(200, Math.floor(rect.width));
      const h = 200;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      canvas!.style.width = `${w}px`;
      canvas!.style.height = `${h}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx!.fillStyle = "#FFFFFF";
      ctx!.fillRect(0, 0, w, h);
      drawBaseline(ctx!, w);
      setInkContext(ctx!);

      if (value) {
        const img = new Image();
        img.onload = () => {
          ctx!.drawImage(img, 0, 0, w, h);
          setInkContext(ctx!);
          setSigned(true);
        };
        img.src = value;
      }
    }

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getPos(e: { clientX: number; clientY: number }): Point {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function sync() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL("image/png"));
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const p = getPos(e);
    lastRef.current = p;
    if (!signed) setSigned(true);

    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      setInkContext(ctx);
      ctx.beginPath();
      ctx.arc(p.x, p.y, INK_WIDTH / 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx || !lastRef.current) return;
    const p = getPos(e);

    setInkContext(ctx);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  }

  function endStroke() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastRef.current = null;
    sync();
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    drawBaseline(ctx, canvas.width / dpr);
    setInkContext(ctx);
    setSigned(false);
    onChange("");
  }

  return (
    <div>
      <div
        ref={wrapRef}
        className="relative w-full overflow-hidden rounded-[10px] border border-dashed border-slate-300 bg-white"
        style={{ height: 200, touchAction: "none" }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
          className="h-full w-full cursor-crosshair"
          aria-label="Customer signature canvas"
        />
        {!signed && (
          <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-slate-400">
            Sign here
          </span>
        )}
      </div>

      <div className="mt-2.5 flex items-center justify-between">
        <Button variant="ghost" size="sm" leftIcon={<RotateCcw className="h-4 w-4" />} onClick={clear}>
          Clear
        </Button>
        {signed ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
            <CheckCircle2 className="h-4 w-4" />
            Signed
          </span>
        ) : (
          <span className="text-xs text-slate-400">Not signed</span>
        )}
      </div>
    </div>
  );
}